import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface MCPServerFormData {
  name: string;
  url: string;
  oauth_enabled: boolean;
}

interface MCPServerFormProps {
  initialData?: MCPServerFormData;
  onSubmit: (data: MCPServerFormData) => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
}

/**
 * Form for creating/editing MCP servers
 * - Mobile-friendly with stacked layout
 * - Validation for required fields
 * - OAuth toggle
 * - Responsive submit/cancel buttons
 */
export function MCPServerForm({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
}: MCPServerFormProps) {
  const [formData, setFormData] = useState<MCPServerFormData>(
    initialData || {
      name: '',
      url: '',
      oauth_enabled: false,
    }
  );
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Server name is required';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'Server URL is required';
    } else if (!isValidUrl(formData.url)) {
      newErrors.url = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Input
          label="Server Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="My MCP Server"
          error={errors.name}
          required
          data-testid="server-name-input"
        />
      </div>

      <div>
        <Input
          label="Server URL"
          type="url"
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          placeholder="https://mcp.example.com"
          error={errors.url}
          required
          data-testid="server-url-input"
        />
        <p className="text-xs text-slate-400 mt-1">
          The base URL for your MCP server (e.g., https://api.example.com/mcp)
        </p>
      </div>

      <div className="flex items-center gap-3 p-4 bg-slate-900 border border-slate-700 rounded-lg">
        <div className="flex-1">
          <label className="block text-sm font-medium text-white mb-1">OAuth Authentication</label>
          <p className="text-xs text-slate-400">
            Enable OAuth for secure authentication with this server
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, oauth_enabled: !formData.oauth_enabled })}
          className={`min-w-[44px] min-h-[44px] px-4 py-2 rounded-lg font-medium transition-colors touch-manipulation ${
            formData.oauth_enabled
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
          data-testid="oauth-toggle"
        >
          {formData.oauth_enabled ? 'On' : 'Off'}
        </button>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
        <Button
          type="button"
          onClick={onCancel}
          variant="secondary"
          className="flex-1 min-h-[44px] touch-manipulation"
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={submitting}
          className="flex-1 min-h-[44px] touch-manipulation"
          data-testid="submit-button"
        >
          {submitting ? 'Saving...' : isEditing ? 'Update Server' : 'Add Server'}
        </Button>
      </div>
    </form>
  );
}
